import { useTranslation } from 'next-i18next';
import React, { ReactElement, useState } from 'react';
import {
  AgenciesMapSearch,
  IAgenciesMapSuggestion,
  mapAutocompleteObjectToSuggestion,
} from '@hotelplan/fdr.regular.fusion.fdr-agency-map-search';
import { FdrAutocompleteDataSource } from '@hotelplan/fdr.regular.fusion.fdr-autocomplete-data-source';
import { useLazyFetchingPagination } from '@hotelplan/libs.hooks';
import {
  FdrAgency,
  FdrAgencyGeoLocation,
  FdrCoordinatesInput,
} from '@hotelplan/supergraph-api';
import { FdrAgencyMap } from 'components/domain/fdr-agency-map';
import { FdrAgencyRecommender } from 'components/domain/fdr-page-components/recommenders/agency/fdr-agency-recommender';
import { FdrAgencyOverviewItem } from 'components/domain/fdr-page-components/recommenders/agency/fdr-flexible-agency-item';
import { FdrAgencyItemFragment } from 'schemas/fragment/recommenders/agency/fdr-agency-recommender.generated';
import { useFdrAgencyOverviewAgenciesQuery } from 'schemas/query/agency-overview/fdr-agency-overview-agencies.generated';
import {
  FdrAgencySuggestionFragment,
  FdrSearchAgenciesDocument,
  FdrSearchAgenciesQuery,
  FdrSearchAgenciesQueryVariables,
} from 'schemas/query/agency-overview/fdr-search-agencies.generated';

const fdrAgencyRecommenderBG = '#F5F1EC';
const AGENCIES_PER_PAGE = 3;

export function FdrAgencyOverviewContent(): ReactElement {
  const [t] = useTranslation('agency');

  const [map, setMap] = useState<google.maps.Map>(undefined);
  const [userLocation, setUserLocation] =
    useState<FdrCoordinatesInput | undefined>(undefined);

  const perPage = AGENCIES_PER_PAGE;

  const queryResult = useFdrAgencyOverviewAgenciesQuery({
    variables: {
      coordinates: userLocation,
    },
  });

  const fdrAgencies = queryResult?.data?.fdrAgenciesOverview?.agencies || [];

  const { items, page, prevPage, nextPage, setPage } =
    useLazyFetchingPagination<FdrAgencyItemFragment>(fdrAgencies, {
      total: queryResult?.data?.fdrAgenciesOverview?.pagination?.total || 0,
      perPage,
    });

  return (
    <>
      <FdrAgencyRecommender
        total={queryResult?.data?.fdrAgenciesOverview?.pagination?.total || 0}
        page={page}
        prevPage={prevPage}
        nextPage={nextPage}
        setPage={setPage}
        bgColor={fdrAgencyRecommenderBG}
        loading={queryResult?.loading}
        customContent={
          <AgenciesMapSearch
            searchAroundMe
            map={map}
            setUserLocation={setUserLocation}
            autocompleteDataSource={
              <FdrAutocompleteDataSource<
                IAgenciesMapSuggestion,
                FdrSearchAgenciesQuery,
                FdrSearchAgenciesQueryVariables,
                FdrAgencySuggestionFragment | FdrAgencyGeoLocation
              >
                queryDocument={FdrSearchAgenciesDocument}
                dataMapper={data =>
                  data
                    ? {
                        items: data?.fdrSearchAgencies?.agencies || [],
                        pagination: data?.fdrSearchAgencies?.pagination,
                      }
                    : undefined
                }
                variablesMapper={variables => ({
                  text: variables?.query || '',
                  page: variables?.page || 0,
                })}
                suggestionMapper={agency => {
                  return mapAutocompleteObjectToSuggestion(agency, t);
                }}
              />
            }
          />
        }
      >
        {items.map((agency, i) => {
          return (
            <FdrAgencyOverviewItem
              key={`agency-recommendation-item-${i}`}
              agency={agency}
            />
          );
        })}
      </FdrAgencyRecommender>
      <FdrAgencyMap
        agencies={fdrAgencies as Array<FdrAgency>}
        fitBounds={!userLocation}
        setMap={setMap}
      />
    </>
  );
}
